import React, { Component } from 'react';
import { connect } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { dismissVerificationPanel } from '../../../actions/auth';
import ResendVerificationButton from './ResendVerificationButton';

/**
 * The post-signup "check your email" state.
 *
 * NON-BLOCKING, DELIBERATELY. The account exists and works the moment Firebase
 * creates it; nothing about this screen is a wall. It is information — it tells
 * the customer that a link is on its way and, crucially, WHICH ADDRESS it went
 * to, which is the one fact they need to spot a typo.
 *
 * WHY IT IS MOUNTED GLOBALLY RATHER THAN INSIDE THE SIGNUP FORMS.
 * Design 01b 4.6.1 puts this state "in both live signup forms". It cannot live
 * there: `AuthenticationPopup.js:20-23` closes the popup the instant
 * `authenticated` flips true, and `render()` returns null when it is — so a
 * panel rendered inside `SignupForm` would flash for one frame and vanish.
 * Rendering it from `MainLayout` off `state.auth.verification.emailSentTo`
 * gives the same single shared component the design asked for, with the two
 * forms unable to drift, and it survives the popup closing. Recorded as a
 * deviation in design.md.
 *
 * MISTYPED ADDRESS. There is no in-app email change here, on purpose. Firebase
 * requires a recent login for `updateEmail`, and `verifyBeforeUpdateEmail` when
 * email-enumeration protection is on — and which of the two this project
 * permits is not established. A half-working email change on a Thai storefront
 * is worse than a clear route to a human, so the copy says plainly what
 * happened and points at support.
 */
class EmailVerificationPanel extends Component {

    render() {
        const { translate, emailSentTo, panelDismissed, authenticated } = this.props;

        // Only after a signup in THIS session, and only until dismissed.
        if (!emailSentTo || panelDismissed || !authenticated) return null;

        return (
            <div className="email-verification-panel" role="status">
                <div className="email-verification-panel__inner">
                    <FontAwesomeIcon icon={['fas', 'envelope']} className="email-verification-panel__icon" />
                    <div className="email-verification-panel__body">
                        <h4 className="email-verification-panel__heading">
                            {translate('EMAIL_VERIFICATION.SENT_HEADING')}
                        </h4>
                        <p className="email-verification-panel__text">
                            {translate('EMAIL_VERIFICATION.SENT_BODY', { email: emailSentTo })}
                        </p>
                        <div className="email-verification-panel__actions">
                            <button
                                type="button"
                                className="btn btn-black"
                                onClick={this.props.dismissVerificationPanel}
                            >
                                {translate('EMAIL_VERIFICATION.START_SHOPPING')}
                            </button>
                            <ResendVerificationButton />
                        </div>
                        <p className="email-verification-panel__note">
                            {translate('EMAIL_VERIFICATION.WRONG_EMAIL')}
                        </p>
                    </div>
                    <button
                        type="button"
                        className="email-verification-panel__close"
                        aria-label={translate('EMAIL_VERIFICATION.DISMISS')}
                        onClick={this.props.dismissVerificationPanel}
                    >
                        <FontAwesomeIcon icon={['fas', 'times']} />
                    </button>
                </div>
            </div>
        );
    }
}

/**
 * @param {object} state - Redux state.
 * @returns {object} Panel visibility inputs plus the translator.
 */
function mapStateToProps(state) {
    return {
        translate: getTranslate(state.locale),
        authenticated: state.auth.authenticated,
        emailSentTo: state.auth.verification.emailSentTo,
        panelDismissed: state.auth.verification.panelDismissed,
    };
}

export default connect(mapStateToProps, { dismissVerificationPanel })(EmailVerificationPanel);
